export const FIX_GUIDES = {
  NotAllowedError: {
    title: "Camera or microphone permission is blocked",

    body:
      "Your browser did not allow access to the camera or microphone. Follow the steps below and then try again.",

    steps: {
      chrome: {
        desktop: [
          {
            label:
              "Open the site permissions",
            detail:
              "Click the lock, camera, or site-settings icon beside the website address.",
          },
          {
            label:
              "Allow Camera and Microphone",
            detail:
              'Set Camera and Microphone permissions to "Allow".',
          },
          {
            label:
              "Reload this page",
            detail:
              "Refresh the page after changing the permissions.",
          },
        ],

        mobile: [
          {
            label:
              "Open the browser site permissions",
            detail:
              "Tap the site information or lock icon beside the website address.",
          },
          {
            label:
              "Allow Camera and Microphone",
            detail:
              "Make sure both permissions are allowed for this site.",
          },
          {
            label:
              "Return and reload",
            detail:
              "Come back to the consultation and refresh the page.",
          },
        ],
      },

      firefox: {
        desktop: [
          {
            label:
              "Open site permissions",
            detail:
              "Click the permissions icon beside the website address.",
          },
          {
            label:
              "Remove any blocked permission",
            detail:
              "Clear blocked Camera or Microphone permissions so Firefox can ask again.",
          },
          {
            label:
              "Reload and choose Allow",
            detail:
              "Refresh the page and allow access when Firefox asks.",
          },
        ],

        mobile: [
          {
            label:
              "Open browser permissions",
            detail:
              "Open Firefox site permissions for this page.",
          },
          {
            label:
              "Allow Camera and Microphone",
            detail:
              "Enable both permissions for this website.",
          },
          {
            label:
              "Reload this page",
            detail:
              "Return to the consultation and refresh.",
          },
        ],
      },

      safari: {
        desktop: [
          {
            label:
              "Open website settings",
            detail:
              "Use Safari's settings for this website.",
          },
          {
            label:
              "Allow Camera and Microphone",
            detail:
              "Set both permissions to Allow.",
          },
          {
            label:
              "Reload the page",
            detail:
              "Refresh the consultation after changing permissions.",
          },
        ],

        mobile: [
          {
            label:
              "Open your device settings",
            detail:
              "Find Safari or your browser's site permission settings.",
          },
          {
            label:
              "Allow Camera and Microphone",
            detail:
              "Make sure the browser is allowed to use the required devices.",
          },
          {
            label:
              "Return and reload",
            detail:
              "Come back to the consultation and refresh the page.",
          },
        ],
      },

      edge: {
        desktop: [
          {
            label:
              "Open site permissions",
            detail:
              "Click the lock or site-settings icon beside the website address.",
          },
          {
            label:
              "Allow Camera and Microphone",
            detail:
              "Change any blocked permission to Allow.",
          },
          {
            label:
              "Reload this page",
            detail:
              "Refresh after changing the permissions.",
          },
        ],

        mobile: [
          {
            label:
              "Open browser site permissions",
            detail:
              "Open the site information for this page.",
          },
          {
            label:
              "Allow Camera and Microphone",
            detail:
              "Enable both permissions.",
          },
          {
            label:
              "Reload this page",
            detail:
              "Return to the consultation and refresh.",
          },
        ],
      },

      other: {
        desktop: [
          {
            label:
              "Open this site's permissions",
            detail:
              "Look for a lock, camera, microphone, or site-settings icon near the browser address bar.",
          },
          {
            label:
              "Allow Camera and Microphone",
            detail:
              "Enable the permissions required for your selected consultation mode.",
          },
          {
            label:
              "Reload the page",
            detail:
              "Refresh after changing the permissions.",
          },
        ],

        mobile: [
          {
            label:
              "Open browser or device permissions",
            detail:
              "Find your browser's permissions in your phone settings.",
          },
          {
            label:
              "Allow the required devices",
            detail:
              "Enable Microphone and, when using Audio & Video mode, Camera.",
          },
          {
            label:
              "Return and reload",
            detail:
              "Come back to the consultation and refresh the page.",
          },
        ],
      },
    },

    note:
      "If you selected Privacy Mode, camera permission is not required. Microphone permission is still required.",
  },

  NotFoundError: {
    title:
      "Required camera or microphone was not found",

    body:
      "Your device could not provide one of the devices needed for the selected consultation mode.",

    steps: {
      other: {
        desktop: [
          {
            label:
              "Check connected devices",
            detail:
              "Make sure your webcam, microphone, or headset is connected correctly.",
          },
          {
            label:
              "Try reconnecting the device",
            detail:
              "If you use a USB device, unplug it and connect it again.",
          },
          {
            label:
              "Reload this page",
            detail:
              "Refresh after reconnecting your devices.",
          },
        ],

        mobile: [
          {
            label:
              "Check your device",
            detail:
              "Make sure the microphone and camera are available and not physically blocked.",
          },
          {
            label:
              "Restart your device if needed",
            detail:
              "A restart can fix temporary hardware detection problems.",
          },
          {
            label:
              "Return and try again",
            detail:
              "Open the consultation again after restarting.",
          },
        ],
      },
    },

    note:
      "If your camera is unavailable but your microphone works, you may choose Audio Only (Privacy Mode).",
  },

  NotReadableError: {
    title:
      "Camera or microphone may be in use",

    body:
      "Another application or browser tab may currently be using your camera or microphone.",

    steps: {
      other: {
        desktop: [
          {
            label:
              "Close other calling applications",
            detail:
              "Close video meeting, calling, recording, or camera applications.",
          },
          {
            label:
              "Close other browser tabs using your devices",
            detail:
              "Another website may still be using your camera or microphone.",
          },
          {
            label:
              "Try the device check again",
            detail:
              "Return to the waiting room and retry after closing the other applications.",
          },
        ],

        mobile: [
          {
            label:
              "Close other applications",
            detail:
              "Fully close other apps that may be using the camera or microphone.",
          },
          {
            label:
              "Return to this consultation",
            detail:
              "Come back to the waiting room and try again.",
          },
        ],
      },
    },

    note:
      "If this continues, restarting the browser or device may release the camera or microphone.",
  },

  SecurityError: {
    title:
      "Browser media access is unavailable",

    body:
      "The browser cannot currently access the camera or microphone securely.",

    steps: {
      other: {
        desktop: [
          {
            label:
              "Check the website connection",
            detail:
              "Make sure the consultation page is using a secure HTTPS connection.",
          },
          {
            label:
              "Check browser permissions",
            detail:
              "Make sure camera and microphone access are not blocked by the browser or operating system.",
          },
          {
            label:
              "Try a supported browser",
            detail:
              "Use an up-to-date version of Chrome, Edge, Firefox, or Safari.",
          },
        ],

        mobile: [
          {
            label:
              "Check browser permissions",
            detail:
              "Make sure your browser is allowed to use the microphone and camera.",
          },
          {
            label:
              "Check the website connection",
            detail:
              "The consultation should be opened over HTTPS.",
          },
          {
            label:
              "Try again",
            detail:
              "Return to the waiting room after correcting the browser permissions.",
          },
        ],
      },
    },

    note:
      "If this issue continues, use another supported browser or device.",
  },

  unknown: {
    title:
      "Unable to access your consultation devices",

    body:
      "Something unexpected happened while checking your camera or microphone.",

    steps: {
      other: {
        desktop: [
          {
            label:
              "Try the device check again",
            detail:
              "Temporary device errors may resolve when you retry.",
          },
          {
            label:
              "Check browser permissions",
            detail:
              "Make sure the required camera and microphone permissions are allowed.",
          },
          {
            label:
              "Restart your browser",
            detail:
              "Close the browser completely and reopen the consultation.",
          },
        ],

        mobile: [
          {
            label:
              "Try again",
            detail:
              "Retry the device check first.",
          },
          {
            label:
              "Check browser permissions",
            detail:
              "Make sure your browser has access to the required devices.",
          },
          {
            label:
              "Restart your device",
            detail:
              "Restart your phone or tablet if the issue continues.",
          },
        ],
      },
    },

    note:
      "If video is the only problem and your microphone works, Audio Only (Privacy Mode) may still be available.",
  },
};

export const detectBrowser = () => {
  if (
    typeof navigator === "undefined"
  ) {
    return "other";
  }

  const userAgent =
    navigator.userAgent || "";

  if (
    userAgent.includes("Edg")
  ) {
    return "edge";
  }

  if (
    userAgent.includes("Firefox")
  ) {
    return "firefox";
  }

  if (
    userAgent.includes("Chrome") &&
    !userAgent.includes("OPR")
  ) {
    return "chrome";
  }

  if (
    userAgent.includes("Safari") &&
    !userAgent.includes("Chrome")
  ) {
    return "safari";
  }

  return "other";
};

export const detectDevice = () => {
  if (
    typeof navigator === "undefined"
  ) {
    return "desktop";
  }

  return /Android|iPhone|iPad|iPod/i.test(
    navigator.userAgent || "",
  )
    ? "mobile"
    : "desktop";
};

export const resolveFixGuide = (
  errorType,
) => {
  const guide =
    FIX_GUIDES[errorType] ||
    FIX_GUIDES.unknown;

  const browser =
    detectBrowser();

  const device =
    detectDevice();

  const browserSteps =
    guide.steps?.[browser];

  const fallbackSteps =
    guide.steps?.other;

  const steps =
    browserSteps?.[device] ||
    fallbackSteps?.[device] ||
    [];

  return {
    ...guide,
    browser,
    device,
    steps,
  };
};